import { SearchResultCard } from "@components/job_search/search_result_card.tsx";
import useDevice from "@hooks/use_device.ts";
import { Deferred, WhenVisible, router } from "@inertiajs/react";
import useTranslation from "@hooks/use_translation.ts";
import useSearchResultsReload from "@hooks/use_search_results_reload.ts";
import { useMemo } from "react";
import Fingerprint1 from "@images/fingerprint_1.svg";
import EmptyState from "@components/empty_state.tsx";
import type { JobPost, SearchParams } from "@/types/job_search/search";
import { Body } from "@sprout/typography.tsx";
import useSubscriptionModal from "@hooks/use_subscription_modal.ts";
import { newJobAlertPath } from "@/utils/new_job_alert_path.ts";
import { ExpandSearchCard } from "./expand_search_card";

const SKELETON_COUNT = 12;

type Props = {
  jobPosts: JobPost[] | undefined;
  browsing: boolean;
  moreResultsAvailable: boolean;
  currentPage: number;
  lastPublishDate: string | null;
  emptyStateMessage: string;
  searchParams: SearchParams;
  createTrackSearchResultClick: (post: JobPost, position: number) => () => void;
  onView: (id: string) => void;
  savedJobsEnabled: boolean;
};

type SearchResultListProps = {
  jobPosts: JobPost[];
  moreResultsAvailable: boolean;
  createTrackSearchResultClick: (post: JobPost, position: number) => () => void;
  onView: (id: string) => void;
  savedJobsEnabled: boolean;
};

const openApplyModal = (url: string) => {
  router.replaceProp("jobPost", undefined);
  fetch(url, {
    headers: { "X-Modal-Request": "true" },
    credentials: "same-origin",
  })
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then((data) => {
      router.replaceProp("boardConfiguration", data.boardConfiguration);
      router.replaceProp("submitPath", data.submitPath);
      router.replaceProp("educationApiUrl", data.educationApiUrl);
      router.replaceProp("dreamJobAvailable", data.dreamJobAvailable);
      // Set jobPost LAST. It's the prop that mounts the apply modal
      // and triggers prefill, so every org-scoped prop it depends (e.g. educationApiUrl)
      // must already be in place.
      router.replaceProp("jobPost", data.jobPost);
    })
    .catch(() => {
      router.visit(url, { preserveScroll: true });
    });
};

const SearchResultList = ({
  jobPosts,
  moreResultsAvailable,
  createTrackSearchResultClick,
  onView,
  savedJobsEnabled,
}: SearchResultListProps) => {
  const { openModal } = useSubscriptionModal();

  const resultCards = useMemo(() => {
    const jobPostCards = jobPosts.map((post, index) => (
      <SearchResultCard
        key={post.id}
        id={post.id}
        title={post.title}
        company={post.companyName}
        logoUrl={post.logoUrl}
        publishedAt={post.firstPublished}
        url={post.viewJobPath ?? post.publicUrl}
        external={!post.viewJobPath}
        locations={post.locations}
        workType={post.workType}
        payRange={post.payRanges}
        trackClick={createTrackSearchResultClick(post, index + 1)}
        viewed={post.viewed}
        onView={onView}
        appliedAt={post.appliedAt}
        onSave={savedJobsEnabled ? () => {} : undefined}
        onCreateAlert={() => openModal(newJobAlertPath(post))}
        onSelect={() => openApplyModal(post.viewJobPath ?? post.publicUrl)}
      />
    ));

    if (jobPosts.length > 0 && !moreResultsAvailable) {
      jobPostCards.push(<ExpandSearchCard key="expand-search-card" />);
    }

    return jobPostCards;
  }, [jobPosts, createTrackSearchResultClick, onView, savedJobsEnabled, openModal]);

  return <>{resultCards}</>;
};

const LoadingResults = () => {
  return (
    <>
      {[...Array(SKELETON_COUNT)].map((_, i) => (
        <SearchResultCard
          key={i}
          id={i.toString()}
          title={""}
          company={""}
          logoUrl={""}
          publishedAt={""}
          url={""}
          loading={true}
          viewed={false}
          onView={() => {}}
          appliedAt={null}
        />
      ))}
    </>
  );
};

const JobSearchTable = ({
  jobPosts,
  browsing,
  moreResultsAvailable,
  currentPage,
  lastPublishDate,
  emptyStateMessage,
  createTrackSearchResultClick,
  searchParams,
  onView,
  savedJobsEnabled,
}: Props) => {
  const { t } = useTranslation("jobSearch");
  const device = useDevice();
  const { reloadParams, loading } = useSearchResultsReload({
    browsing,
    currentPage,
    lastPublishDate,
    searchParams,
  });

  return (
    <>
      {jobPosts?.length === 0 ? (
        <EmptyState
          svg={Fingerprint1}
          svgSize={"150"}
          fontWeight={"400"}
          message={emptyStateMessage}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Deferred fallback={<LoadingResults />} data={"jobPosts"}>
              <SearchResultList
                jobPosts={jobPosts!}
                moreResultsAvailable={moreResultsAvailable}
                createTrackSearchResultClick={createTrackSearchResultClick}
                onView={onView}
                savedJobsEnabled={savedJobsEnabled}
              />
            </Deferred>
            {loading && <LoadingResults />}
          </div>
          <div className="flex flex-col gap-3 justify-center items-center">
            {device.isWeb && <Body light>{t("aiDisclaimer")}</Body>}
            {moreResultsAvailable && (
              <WhenVisible fallback={t("loading")} params={reloadParams} always>
                <div></div>
              </WhenVisible>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default JobSearchTable;
